import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// A single query-string param backing one piece of filter state (player, date, session
// selection) — read on first render, written back on every change. Always merges with
// whatever other params are already in the URL (page, gpsTab, sibling filters set by other
// useUrlParam calls on the same page) rather than replacing the search string wholesale, and
// uses `replace: true` so adjusting a filter doesn't spam browser back-button history the way
// a real navigation would. Scoped to Task 3's three filter kinds (player, date/session-range,
// session-type) — sort columns, chart-metric toggles, and other UI-only state stay local
// useState, per the task's own "purely a filter-state plumbing change" scope.
export function useUrlParam(name, defaultValue = '') {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(name);
  const value = raw === null ? defaultValue : raw;

  const setValue = useCallback((next) => {
    setSearchParams(prev => {
      const merged = new URLSearchParams(prev);
      if (next === null || next === undefined || next === '') merged.delete(name);
      else merged.set(name, String(next));
      return merged;
    }, { replace: true });
  }, [name, setSearchParams]);

  return [value, setValue];
}

// Comma-separated variant for multi-select filters (compare players, session multi-select).
// The returned array is memoized on the raw string — without that, callers get a new array
// reference on every render regardless of whether the selection actually changed, which breaks
// any useMemo/useEffect a page keys off of it (e.g. FutureTargets.js's visiblePlayers ->
// periodsByPlayer -> allDates chain), sending it into a render loop that ends up racing with
// the URL update itself.
export function useUrlParamArray(name) {
  const [raw, setRaw] = useUrlParam(name, '');
  const value = useMemo(() => (raw ? raw.split(',').filter(Boolean) : []), [raw]);
  const setValue = useCallback((next) => setRaw((next || []).join(',')), [setRaw]);
  return [value, setValue];
}
